import { Box, Typography } from '@mui/material';
import { AutocompleteArrayInput, useTranslate } from 'react-admin';
import { useMemo, useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import PromotionTypeSelector from './PromotionTypeSelector.jsx';
import ProductScopeSelector from './ProductScopeSelector.jsx';
import ProductCategoriesInput from './ProductCategoriesInput.jsx';
import SingleProductSelector from './SingleProductSelector.jsx';
import { CampaignProductScope } from '@/constants/CampaignProductScope';
import { resetProductSelectionForScope } from '@/helpers/marketing/resetProductSelectionForScope';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledDefinitionStep = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '492px',
    '& .group-heading': {
        display: 'block',
        fontSize: '10px',
        fontWeight: 700,
        color: Palette.gray.gray800,
        marginBottom: '16px',
        textTransform: 'uppercase',
    },
}));

const DefinitionStep = ({ brandChoices = [] }) => {
    const { setValue } = useFormContext();
    const translate = useTranslate();
    const productScope = useWatch({ name: 'productScope' });
    const watchedBrandIds = useWatch({ name: 'brandIds' });

    const isBrandFilled = useMemo(() => {
        const ids = watchedBrandIds || [];
        return Array.isArray(ids) ? ids.length > 0 : false;
    }, [watchedBrandIds]);

    useEffect(() => {
        resetProductSelectionForScope(productScope, setValue);
    }, [productScope, setValue]);

    const watchedProductId = useWatch({ name: 'productId' });
    useEffect(() => {
        if (productScope === CampaignProductScope.SINGLE && watchedProductId) {
            setValue('productIds', [watchedProductId], { shouldDirty: true });
        }
    }, [watchedProductId, productScope, setValue]);

    return (
        <StyledDefinitionStep>
            <Box>
                <Typography variant='caption' className='group-heading'>
                    {translate('resources.campaign.labels.promotionType')}
                </Typography>
                <PromotionTypeSelector />
            </Box>

            <Box>
                <Typography variant='caption' className='group-heading'>
                    {translate('resources.campaign.labels.productScope')}
                </Typography>
                <ProductScopeSelector />
            </Box>
            {productScope === CampaignProductScope.GROUP && (
                <Box>
                    <Typography variant='caption' className='group-heading'>
                        {translate('resources.campaign.labels.productsInPromotion')}
                    </Typography>
                    <Box className='autocomplete-parent'>
                        <AutocompleteArrayInput
                            source='brandIds'
                            choices={brandChoices}
                            label={translate('resources.campaign.labels.productBrands')}
                            fullWidth
                            disablePortal={true}
                            className={isBrandFilled ? 'input-filled' : ''}
                            sx={{
                                minWidth: '492px',
                                marginBottom: '16px',
                                '& .MuiFormHelperText-root': {
                                    display: 'none',
                                },
                            }}
                        />
                        <ProductCategoriesInput source='categoryIds' label={translate('resources.campaign.labels.productCategories')} />
                    </Box>
                </Box>
            )}

            {productScope === CampaignProductScope.SINGLE && (
                <Box>
                    <Typography variant='caption' className='group-heading'>
                        {translate('resources.campaign.labels.selectedProduct')}
                    </Typography>
                    <SingleProductSelector source='productId' />
                </Box>
            )}
        </StyledDefinitionStep>
    );
};

export default DefinitionStep;
